import { cn } from '@/lib/utils'
import { toneClass, type DsTone } from '@/ds/tone'
import { DsIconGlyph } from '@/ds/icon'
import { DsIcons, type DsIcon } from '@/ds/icons'
import { DsButton } from '@/ds/button'

export interface DsEmptyStateProps {
  title: string
  description?: string
  icon?: DsIcon
  tone?: DsTone
  actionLabel?: string
  onAction?: () => void
  secondaryLabel?: string
  onSecondary?: () => void
  center?: boolean
  compact?: boolean
  className?: string
}

function Base({ title, description, icon = DsIcons.document, tone = 'neutral', actionLabel, onAction, secondaryLabel, onSecondary, center = true, compact, className }: DsEmptyStateProps) {
  const t = toneClass[tone]
  return (
    <div className={cn('flex flex-col', center && 'items-center text-center', compact ? 'gap-2 py-4' : 'gap-3 py-8', className)}>
      <span className={cn('inline-flex items-center justify-center rounded-2xl', compact ? 'size-12' : 'size-16', t.soft, t.onSoft)}>
        <DsIconGlyph icon={icon} size={compact ? 22 : 28} />
      </span>
      <div className={cn('flex flex-col gap-1', center && 'items-center')}>
        <p className="text-title3 text-foreground">{title}</p>
        {description && <p className="max-w-sm text-small text-muted-foreground">{description}</p>}
      </div>
      {(actionLabel || secondaryLabel) && (
        <div className={cn('mt-1 flex flex-wrap gap-2', center && 'justify-center')}>
          {actionLabel && <DsButton label={actionLabel} onClick={onAction} size={compact ? 'sm' : 'md'} />}
          {secondaryLabel && <DsButton variant="ghost" label={secondaryLabel} onClick={onSecondary} size={compact ? 'sm' : 'md'} />}
        </div>
      )}
    </div>
  )
}

type Preset = Omit<DsEmptyStateProps, 'icon' | 'tone'> & { icon?: DsIcon }

export const DsEmptyState = Object.assign(Base, {
  NoResults: (p: Preset) => <Base icon={DsIcons.search} tone="neutral" {...p} />,
  Error: (p: Preset) => <Base icon={DsIcons.warning} tone="danger" {...p} />,
  Offline: (p: Preset) => <Base icon={DsIcons.wifiOff} tone="warning" {...p} />,
})
